import { useDeferredValue, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ITEM_SORTS, type ItemSummary } from "@sable/api-contract";
import { IconBox, IconGrid, IconList, IconSearch } from "../components/icons.tsx";
import { Kpis } from "../components/Kpis.tsx";
import { Empty, ErrorBlock, Loading, MarketplaceMark, StatusPill, StatusTag } from "../components/ui.tsx";
import { t, tk } from "../i18n/index.ts";
import { money } from "../lib/format.ts";
import { useItems, useMarketplaces, type ItemFilters } from "../lib/queries.ts";
import { useCan, useWs, wsPath } from "../lib/workspace.tsx";

const FILTERS = ["active", "in_stock", "listed", "sold", "shipped", "all"] as const;
type Filter = (typeof FILTERS)[number];
const VIEWS = ["list", "grid"] as const;
type View = (typeof VIEWS)[number];

function countFor(filter: Filter, counts: Record<string, number> | undefined): number | null {
  if (!counts) return null;
  const n = (k: string) => counts[k] ?? 0;
  switch (filter) {
    case "active": return n("draft") + n("in_stock") + n("listed");
    case "all": return Object.values(counts).reduce((a, b) => a + b, 0);
    default: return n(filter);
  }
}

function priceOf(item: ItemSummary): number | null {
  return item.salePriceMinor ?? item.listPriceMinor;
}

/** The storefronts an item is live on, one coloured mark each. */
function Marks({ codes }: { codes: string[] }) {
  const mps = useMarketplaces();
  if (codes.length === 0) return <span className="mk" />;
  const names = codes.map((c) => mps.data?.find((m) => m.code === c)?.name ?? c);
  return (
    <span className="mk" role="img" aria-label={t("inventory.listedOn", { names: names.join(", ") })} title={names.join(", ")}>
      {codes.map((c) => <MarketplaceMark key={c} code={c} />)}
    </span>
  );
}

function ItemRow({ item }: { item: ItemSummary }) {
  const ws = useWs();
  const price = priceOf(item);
  const meta = [item.brand, item.sizeLabel, item.sku].filter(Boolean).join(" · ");
  return (
    <Link className="inv-row" to={wsPath(ws, `/items/${item.id}`)}>
      <span className="inv-thumb">
        {item.thumbUrl ? <img src={item.thumbUrl} alt="" loading="lazy" /> : <IconBox size={20} />}
      </span>
      <span className="inv-text">
        <span className="title">{item.title || t("inventory.untitled")}</span>
        <span className="meta">{meta}</span>
      </span>
      <Marks codes={item.marketplaces} />
      <span className="inv-price num">{price !== null ? money(price, item.currency) : <span className="muted">—</span>}</span>
      <StatusTag status={item.status} />
    </Link>
  );
}

function ItemCard({ item }: { item: ItemSummary }) {
  const ws = useWs();
  const price = priceOf(item);
  const meta = [item.brand, item.sizeLabel, item.sku].filter(Boolean).join(" · ");
  return (
    <Link className="item-card" to={wsPath(ws, `/items/${item.id}`)}>
      <div className="thumb">
        {item.thumbUrl ? <img src={item.thumbUrl} alt="" loading="lazy" /> : <div className="thumb-empty"><IconBox size={28} /></div>}
        <StatusPill status={item.status} />
      </div>
      <div className="body">
        <div className="title">{item.title || t("inventory.untitled")}</div>
        <div className="meta">{meta}</div>
        <div className="spread">
          <span className="price">{price !== null ? money(price, item.currency) : <span className="muted small">—</span>}</span>
          {item.purchasePriceMinor !== null && (
            <span className="muted small num">{t("inventory.cost", { amount: money(item.purchasePriceMinor, item.currency) })}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

export function Inventory() {
  const ws = useWs();
  const canFinance = useCan("finance.read");
  const canList = useCan("listings.write");
  const [params, setParams] = useSearchParams();
  const status = (FILTERS as readonly string[]).includes(params.get("status") ?? "") ? (params.get("status") as Filter) : "active";
  const sort = (ITEM_SORTS as readonly string[]).includes(params.get("sort") ?? "") ? (params.get("sort") as ItemFilters["sort"]) : "newest";
  const archived = params.get("archived") === "include" ? "include" : "exclude";
  const view: View = params.get("view") === "grid" ? "grid" : "list";
  const [search, setSearch] = useState(params.get("q") ?? "");
  const q = useDeferredValue(search.trim());

  const filters: ItemFilters = { status, sort, archived, q: q || undefined };
  const items = useItems(ws.id, filters);
  const pages = items.data?.pages ?? [];
  const list = pages.flatMap((p) => p.items);
  const counts = pages[0]?.counts;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t("inventory.title")}</h1>
        </div>
        <div className="row-wrap">
          {canList && <Link className="btn btn-ghost" to={wsPath(ws, "/shops")}>{t("shops.nav")}</Link>}
          <Link className="btn btn-primary" to={wsPath(ws, "/items/new")}>{t("nav.add")}</Link>
        </div>
      </div>

      {canFinance && <Kpis />}

      <div className="toolbar">
        <label className="search">
          <IconSearch size={18} />
          <span className="visually-hidden">{t("inventory.search")}</span>
          <input
            className="input"
            type="search"
            placeholder={t("inventory.search")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setParam("q", e.target.value.trim() || null);
            }}
          />
        </label>
        <div className="chips" role="group">
          {FILTERS.map((f) => {
            const n = countFor(f, counts);
            return (
              <button key={f} className="chip" aria-pressed={status === f} onClick={() => setParam("status", f === "active" ? null : f)}>
                {tk("status", f)}
                {n !== null && <span className="count num">{n}</span>}
              </button>
            );
          })}
        </div>
        <div className="toolbar-row">
          <label className="check small">
            <input type="checkbox" checked={archived === "include"} onChange={(e) => setParam("archived", e.target.checked ? "include" : null)} />
            {t("inventory.showArchived")}
          </label>
          <div className="row">
            <label className="row small">
              <span className="muted">{t("inventory.sort")}</span>
              <select className="select" value={sort} onChange={(e) => setParam("sort", e.target.value === "newest" ? null : e.target.value)}>
                {ITEM_SORTS.map((s) => <option key={s} value={s}>{tk("inventory.sort", s)}</option>)}
              </select>
            </label>
            <div className="seg" role="group" aria-label={t("inventory.view")}>
              {VIEWS.map((v) => (
                <button key={v} className="seg-btn" aria-pressed={view === v} aria-label={t(`inventory.view.${v}`)} title={t(`inventory.view.${v}`)}
                  onClick={() => setParam("view", v === "list" ? null : v)}>
                  {v === "list" ? <IconList size={18} /> : <IconGrid size={18} />}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {items.isPending ? (
        <Loading />
      ) : items.isError ? (
        <ErrorBlock error={items.error} onRetry={() => void items.refetch()} />
      ) : list.length === 0 ? (
        <Empty
          title={t("inventory.empty")}
          body={status === "active" && !q ? t("inventory.emptyAll") : undefined}
          action={status === "active" && !q ? <Link className="btn btn-primary" to={wsPath(ws, "/items/new")}>{t("nav.add")}</Link> : undefined}
        />
      ) : (
        <div className="stack-lg">
          {view === "list" ? (
            <div className="inv-rows">
              {list.map((item) => <ItemRow key={item.id} item={item} />)}
            </div>
          ) : (
            <div className="item-grid">
              {list.map((item) => <ItemCard key={item.id} item={item} />)}
            </div>
          )}
          {items.hasNextPage && (
            <button className="btn" disabled={items.isFetchingNextPage} onClick={() => void items.fetchNextPage()}>
              {items.isFetchingNextPage ? t("app.loading") : t("inventory.loadMore")}
            </button>
          )}
        </div>
      )}
    </>
  );
}
